import { useState } from "react";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/contexts/LanguageContext";
import { Lightbulb, Sparkles } from "lucide-react";

const ProblemStep = ({ project, setProject, saveProject, goToStep }) => {
  const { t, language } = useLanguage();
  const [localProblem, setLocalProblem] = useState(project?.problem_statement || "");
  const [isSaving, setIsSaving] = useState(false);

  const handleSave = async () => {
    if (!localProblem.trim()) return;
    
    setIsSaving(true);
    const success = await saveProject({ 
      problem_statement: localProblem,
      current_step: 1
    });
    setIsSaving(false);
    
    if (success) {
      setProject(prev => ({ ...prev, problem_statement: localProblem }));
    }
  };

  const handleContinue = async () => {
    if (!localProblem.trim()) return;
    
    await handleSave();
    goToStep(2);
  };

  const placeholders = language === 'es' ? [
    "¿Cómo podríamos reducir el desperdicio de alimentos en áreas urbanas?",
    "El problema principal es que las pequeñas empresas luchan por acceder a herramientas de marketing asequibles...",
    "Queremos ayudar a los equipos remotos a colaborar más efectivamente en proyectos creativos...",
    "¿Cómo podemos hacer que la moda sostenible sea más accesible para los consumidores comunes?",
  ] : [
    "How might we reduce food waste in urban areas?",
    "The main problem is that small businesses struggle to access affordable marketing tools...",
    "We want to help remote teams collaborate more effectively on creative projects...",
    "How can we make sustainable fashion more accessible to everyday consumers?",
  ];

  const randomPlaceholder = placeholders[Math.floor(Math.random() * placeholders.length)];

  return (
    <div className="h-full flex flex-col items-center justify-center p-4 sm:p-8">
      <div className="w-full max-w-3xl">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-primary-light rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Lightbulb className="w-8 h-8 text-primary" />
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl font-semibold text-stone-900 mb-3">
            {t('problem_title')}
          </h1>
          <p className="text-lg text-stone-600 max-w-xl mx-auto">
            {t('problem_description')}
          </p>
        </div>

        {/* Input Area */}
        <div className="bg-white rounded-2xl border border-stone-200 shadow-card p-6 sm:p-8">
          <Textarea
            value={localProblem}
            onChange={(e) => setLocalProblem(e.target.value)}
            placeholder={randomPlaceholder}
            className="input-premium min-h-[200px] text-lg sm:text-xl border-none focus:ring-0 resize-none"
            data-testid="problem-input"
          />
          
          {/* Tips */}
          <div className="mt-6 p-4 bg-primary-light/50 rounded-xl">
            <h4 className="font-medium text-stone-700 mb-2 flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-primary" />
              {t('problem_tips_title')}
            </h4>
            <ul className="text-sm text-stone-600 space-y-1">
              <li>• {t('problem_tip_1')}</li>
              <li>• {t('problem_tip_2')}</li>
              <li>• {t('problem_tip_3')}</li>
            </ul>
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-center mt-8 gap-4">
          <Button
            onClick={handleSave}
            disabled={!localProblem.trim() || isSaving}
            variant="outline"
            className="btn-secondary"
            data-testid="save-problem-btn"
          >
            {isSaving ? t('common_saving') : t('problem_save_draft')}
          </Button>
          <Button
            onClick={handleContinue}
            disabled={!localProblem.trim() || isSaving}
            className="btn-primary"
            data-testid="continue-to-ideas-btn"
          >
            {t('problem_generate')}
            <Sparkles className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ProblemStep;
